import { useEffect } from 'react';

import { Text } from '@atom/typography/typography';
import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { IntegrationBaseForm } from '@form/integration-base/integration-base.form';
import { useGetConnectionQuery } from '@hook/connection/connection.hooks';
import { useStoreSelector } from '@hook/store/store.hooks';
import { ConnectorInterface } from '@interface/connector/connector.interface';
import { BaseFormInterface } from '@interface/form/base-form.interface';
import { ErrorFormInterface } from '@interface/form/error-form.interface';
import { isExist, isString } from '@util/data/is-exist';

import { getIntegrationFields } from './integration-base.fields-map';

const CONNECTIONS_WITH_WEBHOOK_URL: ConnectorServiceEnum[] = [ConnectorServiceEnum.Elisa];
const CONNECTIONS_WITH_SCIM_METADATA: ConnectorServiceEnum[] = [ConnectorServiceEnum.OktaScim];

interface IntegrationBaseProps extends BaseFormInterface {
  connector: ConnectorInterface;
  connectionId?: string;
}

export const IntegrationBase = ({ form, connector, connectionId }: IntegrationBaseProps) => {
  const connections = useStoreSelector((state) => state.connections.data);
  const connectionFromStore = connectionId ? connections[connectionId] : undefined;
  const { data, error } = useGetConnectionQuery(connector, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    skip: isExist(connectionFromStore),
  });

  const connectionData = connectionFromStore ?? data?.data;

  const showWebhookUrl =
    CONNECTIONS_WITH_WEBHOOK_URL.includes(connector.serviceId) &&
    isString(connectionData?.metadata?.webhookDeliveryUrl);

  const showScimMetadata = CONNECTIONS_WITH_SCIM_METADATA.includes(connector.serviceId);

  useEffect(() => void (isExist(connectionData) && form.setFieldsValue(connectionData)), [connectionData]);

  return (
    <>
      {showWebhookUrl && (
        <Text color={ColorEnum.Black100} level='m' mb={16} paragraph>
          Webhook URL: {connectionData!.metadata!.webhookDeliveryUrl}
        </Text>
      )}
      {showScimMetadata && isString(connectionData?.metadata?.apiToken) && (
        <Text color={ColorEnum.Black100} level='m' mb={16} paragraph>
          API Token: {connectionData!.metadata!.apiToken as string}
        </Text>
      )}
      {showScimMetadata && isString(connectionData?.metadata?.baseUrl) && (
        <Text color={ColorEnum.Black100} level='m' mb={16} paragraph>
          Base URL: {connectionData!.metadata!.baseUrl as string}
        </Text>
      )}
      <IntegrationBaseForm
        error={error as ErrorFormInterface}
        fields={getIntegrationFields(connector.serviceId)}
        form={form}
        serviceId={connector.serviceId}
      />
    </>
  );
};
